import {ChangeDetectionStrategy, Component, computed, Injectable, input, Signal} from "@angular/core";
import {AkseptertSignaturmetode, DokumentId, DokumentSignatarerModell, DokumentVersjonModell, LitenIkkeTomStreng} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {firstValueFrom, Observable, switchMap} from "rxjs";
import {toObservable, toSignal} from "@angular/core/rxjs-interop";
import {SigneringStatusKomponent} from "../../../signering/komponenter/signering-status/signering-status";
import {DokumentSigneringDatatjeneste} from "felles-ng2/tjenester/datalager/DokumentSigneringDatatjeneste";
import {tomDokumentSignatarerModell} from "felles-ng2/modell/tommeModeller";
import {TranslateModule} from "@ngx-translate/core";
import {MatButton} from "@angular/material/button";
import {MatTooltip} from "@angular/material/tooltip";
import {BekreftKlikkDirektiv} from "web-ng2/delt/direktiver/button--bekreftKlikk/button--bekreftKlikk";
import {MatDialog} from "@angular/material/dialog";
import {SigneringAvsluttDialogKomponent} from "../../../signering/modale/signering-avslutt-dialog/signering-avslutt-dialog";
import {SigneringBekreftSlettSignaturerDialogKomponent} from "../../../signering/modale/signering-bekreft-slett-signaturer-dialog/signering-bekreft-slett-signaturer-dialog";
import {signatarerSomHarSignert} from "felles-ng2/modell/dokumentSignatarerModellHjelpere";
import {asynkArbeidPågårSignal} from "felles-ng2/tjenester/AsynkArbeidPågårSignal";
import {TittelVerdiKomponent, VerdiKomponent} from "web-ng2/delt/komponenter/tittel-verdi/tittel-verdi";
import {DokumentRedigerTilSigneringDialogKomponent} from "web-ng2/moduler/dokument/modale/dokument-rediger-til-signering-dialog/dokument-rediger-til-signering-dialog";
import {DokumentSigneringTjeneste} from "web-ng2/moduler/dokument/tjenester/DokumentSigneringTjeneste";

const debug = initDebugger('DokumentTilSigneringStatusKomponent');

/**
 * Viser signeringsstatus for dokument sendt til signering.
 */
@Component({
    selector: 'dokument-til-signering-status',
    templateUrl: 'dokument-til-signering-status.html',
    styleUrl: 'dokument-til-signering-status.scss',
    imports: [
        SigneringStatusKomponent,
        TranslateModule,
        MatButton,
        MatTooltip,
        BekreftKlikkDirektiv,
        TittelVerdiKomponent,
        VerdiKomponent
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DokumentTilSigneringStatusKomponent {

    dokumentversjon = input.required<DokumentVersjonModell>();

    signatarer: Signal<DokumentSignatarerModell>;
    avansertSignaturmetodeTilgjengelig = computed(() => this.dokumentversjon().aksepterteSignaturmetoder.includes(AkseptertSignaturmetode.avansertelektronisk));
    enkelSignaturmetodeTilgjengelig = computed(() => this.dokumentversjon().aksepterteSignaturmetoder.includes(AkseptertSignaturmetode.enkelelektronisk));
    redigerMulig = computed(() => this.dokumentSigneringTjeneste.redigerSignatarerMulig(this.dokumentversjon()));
    avsluttMulig = computed(() => this.dokumentSigneringTjeneste.avsluttSigneringMulig(this.dokumentversjon()));
    kansellerMulig = computed(() => this.dokumentSigneringTjeneste.kansellerSigneringMulig(this.dokumentversjon()));

    laster = asynkArbeidPågårSignal();

    constructor(private avhengigheter: DokumentTilSigneringStatusKomponentAvhengigheter, private dokumentSigneringTjeneste: DokumentSigneringTjeneste, private dialog: MatDialog) {
        this.signatarer = toSignal(toObservable(this.dokumentversjon)
            .pipe(switchMap(dokumentversjon => this.avhengigheter.signatarer(dokumentversjon.dokumentId))),
          {initialValue: tomDokumentSignatarerModell()});
    }

    rediger() {
        DokumentRedigerTilSigneringDialogKomponent.vis(this.dialog, this.dokumentversjon());
    }

    async kansellerSignering() {
        const respons = await firstValueFrom(SigneringBekreftSlettSignaturerDialogKomponent.vis(this.dialog, signatarerSomHarSignert(this.signatarer()))
          .beforeClosed());
        if (!respons!.utfør) return;
        await this.laster.utfør(() => this.avhengigheter.kanseller(this.dokumentversjon(), respons!.årsak));
    }

    async avsluttSignering() {
        await firstValueFrom(SigneringAvsluttDialogKomponent.vis(this.dialog, this.dokumentversjon())
          .afterClosed())
    }
}

@Injectable()
export class DokumentTilSigneringStatusKomponentAvhengigheter {

    constructor(private dokumentSigneringDatatjeneste: DokumentSigneringDatatjeneste) {
    }

    signatarer(id: DokumentId): Observable<DokumentSignatarerModell> {
        return this.dokumentSigneringDatatjeneste.hent(id);
    }

    async kanseller(dokumentversjon: DokumentVersjonModell, årsak: LitenIkkeTomStreng): Promise<void> {
        await this.dokumentSigneringDatatjeneste.kanseller(dokumentversjon, årsak);
    }

}
